/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react'
import { Form } from 'react-bootstrap'
import { Card } from 'react-bootstrap-v5'
import { Link } from 'react-router-dom'
import Select from 'react-select'
import { getRequestOptions } from '../helpers/Fetchwrapper'
import { numberFormat } from '../helpers/GlobalHelper'
import GetPagePermission from '../components/GetPagePermission'
import DataTable from '../components/DataTable'

const ALL_WAREHOUSES = { label: 'All Warehouses', value: '' }

const ITEM_TYPE_OPTIONS = [
	{ label: 'All', value: '' },
	{ label: 'Raw Material', value: 'raw_material' },
	{ label: 'Finished Product', value: 'finished_product' },
]

// Labels match the Type column returned by reports/stock-transactions/dt
const TRANSACTION_TYPE_OPTIONS = [
	{ label: 'All', value: '' },
	...['Purchase', 'Opening', 'Warehouse Received', 'Warehouse Transfer', 'Production', 'Blending', 'Packing',
		'Issue', 'Sales', 'Sales Return', 'Adjustment'].map((type) => ({ label: type, value: type })),
]

const toInputDate = (date: Date) => {
	const month = String(date.getMonth() + 1).padStart(2, '0')
	const day = String(date.getDate()).padStart(2, '0')
	return `${date.getFullYear()}-${month}-${day}`
}

const referencePath = (row: any): string | null => {
	if (row.id_purchase) return `/purchase-details/${row.id_purchase}`
	if (row.id_inv_opening_stocks) return `/inventory-opening-details/${row.id_inv_opening_stocks}`
	if (row.id_inv_warehouse_transfers) return `/warehouse-transfer-details/${row.id_inv_warehouse_transfers}`
	if (row.id_item_stock_adjustments) return `/item-stock-adjustment-details/${row.id_item_stock_adjustments}`
	if (row.id_productions) return `/manufacturing-details/${row.id_productions}`
	if (row.id_blending_orders) return `/blending-order-details/${row.id_blending_orders}`
	if (row.id_packing_orders) return `/packing-order-details/${row.id_packing_orders}`
	if (row.id_inv_issues) return `/stock-issue-details/${row.id_inv_issues}`
	if (row.id_shipments) return `/shipments?shipmentId=${row.id_shipments}`
	if (row.id_sales_orders) return `/sales-order/${row.id_sales_orders}`
	return null
}

const qty = (value: any) => (Number(value) ? numberFormat(value) : '-')

const StockTransactionReport: React.FC = () => {
	const hasPermission = GetPagePermission('/stock-transaction-report')

	const today = new Date()
	const [projects, setProjects] = useState<any[]>([])
	const [project, setProject] = useState<any>(null)
	const [warehouses, setWarehouses] = useState<any[]>([])
	const [warehouse, setWarehouse] = useState<any>(ALL_WAREHOUSES)
	const [itemType, setItemType] = useState<any>(ITEM_TYPE_OPTIONS[0])
	const [txnType, setTxnType] = useState<any>(TRANSACTION_TYPE_OPTIONS[0])
	const [fromDate, setFromDate] = useState(toInputDate(new Date(today.getFullYear(), today.getMonth(), 1)))
	const [toDate, setToDate] = useState(toInputDate(today))

	const projectValue = project?.value ? String(project.value) : ''
	const warehouseValue = warehouse?.value ? String(warehouse.value) : ''

	useEffect(() => {
		fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				const options = (resp.data || []).map((row: any) => ({ label: row.name, value: row.id }))
				setProjects(options)
				if (options.length) setProject(options[0])
			})
			.catch((error) => console.log(error, 'catch the hoop'))
	}, [])

	useEffect(() => {
		if (!projectValue) {
			setWarehouses([])
			return
		}
		fetch(process.env.REACT_APP_API_URL + 'warehouse/' + projectValue + '/list', getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				setWarehouses(resp.success && Array.isArray(resp.data)
					? resp.data.map((row: any) => ({ label: row.name, value: String(row.id_warehouses) }))
					: [])
			})
			.catch(() => setWarehouses([]))
	}, [projectValue])

	const filterParams = useMemo(() => ({
		id_projects: projectValue,
		id_warehouses: warehouseValue,
		item_type: itemType?.value || '',
		txn_type: txnType?.value || '',
		from_date: fromDate,
		to_date: toDate,
	}), [projectValue, warehouseValue, itemType, txnType, fromDate, toDate])

	const nameLink = (row: any) => {
		const params = new URLSearchParams({ id_projects: projectValue })
		if (warehouseValue) params.set('id_warehouses', warehouseValue)
		return row.id_products
			? `/finished-goods-stock-details?id_products=${row.id_products}&${params.toString()}`
			: `/item-stock-details?id_items=${row.id_items}&${params.toString()}`
	}

	const columns = [
		{ value: 'txn_date', title: 'Date', style: { textAlign: 'left', whiteSpace: 'nowrap' } },
		{ value: 'txn_type', title: 'Type', style: { textAlign: 'left' } },
		{
			value: 'reference_no',
			title: 'Reference',
			style: { textAlign: 'left' },
			hasHtmlComponent: true,
			htmlComponent: (row: any) => {
				const path = referencePath(row)
				if (!row.reference_no) return '-'
				return path
					? <Link to={path} target='_blank' rel='noopener noreferrer'>{row.reference_no}</Link>
					: row.reference_no
			},
		},
		{ value: 'item_type', title: 'Item Type', style: { textAlign: 'left' } },
		{
			value: 'name',
			title: 'Item / Product',
			style: { textAlign: 'left' },
			hasHtmlComponent: true,
			htmlComponent: (row: any) => <Link to={nameLink(row)}>{row.name}</Link>,
		},
		{ value: 'code', title: 'Code', style: { textAlign: 'left' } },
		{ value: 'packing', title: 'Packing', style: { textAlign: 'left' } },
		{ value: 'size', title: 'Size', style: { textAlign: 'left' } },
		{ value: 'unit_name', title: 'Unit', style: { textAlign: 'left' } },
		{ value: 'warehouse_name', title: 'Warehouse', style: { textAlign: 'left' } },
		{ value: 'in_qty', title: 'In Qty', style: { textAlign: 'right' }, hasHtmlComponent: true, htmlComponent: (row: any) => qty(row.in_qty) },
		{ value: 'out_qty', title: 'Out Qty', style: { textAlign: 'right' }, hasHtmlComponent: true, htmlComponent: (row: any) => qty(row.out_qty) },
	]

	const filters = columns.reduce((acc: Record<string, { filterType: string; filterValue: string }>, column) => {
		acc[column.value] = { filterType: 'like', filterValue: '' }
		return acc
	}, {})

	const canLoad = !!projectValue && !!toDate && !(fromDate && fromDate > toDate)

	if (hasPermission !== 1) {
		return null
	}

	return (
		<Card className='mb-4'>
			<Card.Body>
				<Card.Title className='h5 mb-1'>Stock Transactions</Card.Title>
				<p className='text-muted small mb-3'>
					Every stock movement line: purchase, opening, warehouse transfer and receipt, production, blending, packing, issue, sales, sales return and adjustment.
				</p>
				<div className='row align-items-end'>
					<div className='col-md-3'>
						<Form.Group className='mb-3' controlId='project'>
							<Form.Label>Project<span className='required'></span></Form.Label>
							<Select
								value={project}
								onChange={(selected: any) => {
									setProject(selected)
									setWarehouse(ALL_WAREHOUSES)
								}}
								options={projects}
								menuPosition='fixed'
								styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
								placeholder='Select Project'
							/>
						</Form.Group>
					</div>
					<div className='col-md-3'>
						<Form.Group className='mb-3' controlId='warehouse'>
							<Form.Label>Warehouse</Form.Label>
							<Select
								value={warehouse}
								onChange={(selected: any) => setWarehouse(selected || ALL_WAREHOUSES)}
								options={[ALL_WAREHOUSES, ...warehouses]}
								menuPosition='fixed'
								styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
								isDisabled={!projectValue}
							/>
						</Form.Group>
					</div>
					<div className='col-md-3'>
						<Form.Group className='mb-3' controlId='itemType'>
							<Form.Label>Item Type</Form.Label>
							<Select
								value={itemType}
								onChange={(selected: any) => setItemType(selected || ITEM_TYPE_OPTIONS[0])}
								options={ITEM_TYPE_OPTIONS}
								menuPosition='fixed'
								styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
							/>
						</Form.Group>
					</div>
					<div className='col-md-3'>
						<Form.Group className='mb-3' controlId='txnType'>
							<Form.Label>Transaction Type</Form.Label>
							<Select
								value={txnType}
								onChange={(selected: any) => setTxnType(selected || TRANSACTION_TYPE_OPTIONS[0])}
								options={TRANSACTION_TYPE_OPTIONS}
								menuPosition='fixed'
								styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
							/>
						</Form.Group>
					</div>
					<div className='col-md-3'>
						<Form.Group className='mb-3' controlId='fromDate'>
							<Form.Label>From Date</Form.Label>
							<Form.Control type='date' value={fromDate} max={toDate} onChange={(e) => setFromDate(e.target.value)} />
						</Form.Group>
					</div>
					<div className='col-md-3'>
						<Form.Group className='mb-3' controlId='toDate'>
							<Form.Label>To Date<span className='required'></span></Form.Label>
							<Form.Control type='date' value={toDate} min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)} />
						</Form.Group>
					</div>
					<div className='col-md-3 mb-3'>
						<a
							className={`btn btn-sm btn-primary w-100 py-3 text-nowrap${canLoad ? '' : ' disabled'}`}
							href={canLoad ? `${process.env.REACT_APP_BACKEND}stock_transactions_report_download?${new URLSearchParams(filterParams).toString()}` : undefined}
						>
							<i className='fas fa-download me-1'></i>Excel Download
						</a>
					</div>
				</div>

				{canLoad ? (
					// Remount on filter change so paging restarts from page 1
					<DataTable
						key={JSON.stringify(filterParams)}
						url={process.env.REACT_APP_API_URL + 'reports/stock-transactions/dt'}
						columns={columns}
						filters={filters}
						additionalPostData={filterParams}
						defaultSortedColumn='txn_date'
						defaultSortOrder='ASC'
						defaultPerPage={100}
					/>
				) : (
					<div className='alert alert-info mb-0 text-center'>
						{projectValue ? 'From Date cannot be after To Date.' : 'Please select a project.'}
					</div>
				)}
			</Card.Body>
		</Card>
	)
}

export default StockTransactionReport
